'use client'

import type { InboxFramework } from '@ordo/constants/notification-rules'
import type {
  InboxNotification,
  NotificationDetailBlocks,
  NotificationDetailContent,
  NotificationDetailMessage,
} from '@ordo/notifications-client/contract'
import { ArrowRight, Check, Circle, CircleCheck, Lock, Trash2, WifiOff } from 'lucide-react'
import * as React from 'react'

import { cn } from '../cn'
import {
  bundleLineText,
  bundleSentence,
  groupAppLabels,
  type InboxGroupEntry,
  type InboxItemEntry,
  itemKindLabel,
  NOTIFICATION_ACTION_LABELS,
  NOTIFICATION_BUCKET_LABELS,
  notificationAppLabel,
  type NotificationQuickAction,
  notificationTimeLabel,
  openLabel,
  resolvedLine,
  secondaryOpenLabel,
  uniqueSenders,
} from '../notification-inbox'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './dropdown-menu'
import {
  NotificationAppTile,
  NotificationGenericCircle,
  NotificationPicture,
  NotificationSentence,
} from './notification-picture'

/**
 * What the right side is showing. `null` (or no `loadDetail`) draws the Generic
 * framework from what every notification stores. 403 from the app is
 * `no-access`, 404 is `deleted`, a timeout or 5xx is `unavailable`.
 */
export type NotificationDetailState =
  | { status: 'loading' }
  | { status: 'ready'; content: NotificationDetailContent }
  | { status: 'deleted'; note?: string }
  | { status: 'no-access' }
  | { status: 'unavailable' }

/** After Approve / Decline: the result shows, fades out, then stays only as "· Approved by you". */
export interface NotificationQuickAnswer {
  action: NotificationQuickAction
  text: string
  phase: 'shown' | 'fading' | 'done'
  failed?: boolean
}

export function quickAnswerNote(answer: NotificationQuickAnswer | undefined): string {
  if (!answer || answer.failed || answer.phase !== 'done') {
    return ''
  }
  const past = NOTIFICATION_ACTION_LABELS[answer.action].past
  return ` · ${past.charAt(0).toUpperCase()}${past.slice(1)} by you`
}

export interface NotificationDetailProps {
  /** A notification row, or a merged Following row (drawn as a Timeline). */
  entry: InboxItemEntry
  state?: NotificationDetailState | null
  unread?: boolean
  answer?: NotificationQuickAnswer
  unfollowed?: boolean
  now?: Date
  className?: string
  onOpen?: (notification: InboxNotification, target: 'primary' | 'secondary') => void
  onToggleRead?: () => void
  onUnfollow?: () => void
  onFollowAgain?: () => void
  onAnswer?: (action: NotificationQuickAction) => void
  onReply?: (notification: InboxNotification, text: string) => Promise<void>
  onRetry?: () => void
}

const BUTTON =
  'inline-flex shrink-0 items-center justify-center gap-2 font-semibold transition-colors disabled:opacity-50'
const PRIMARY_OPEN = 'bg-foreground text-background hover:bg-foreground/90'
const OUTLINE = 'border border-border bg-transparent text-foreground hover:bg-muted'

/** The desktop right side: one fixed framework per type, filled by the owning app. */
export function NotificationDetail({
  entry,
  state = null,
  unread = false,
  answer,
  unfollowed = false,
  now = new Date(),
  className,
  onOpen,
  onToggleRead,
  onUnfollow,
  onFollowAgain,
  onAnswer,
  onReply,
  onRetry,
}: NotificationDetailProps) {
  if (state?.status === 'loading') {
    return <NotificationDetailSkeleton className={className} />
  }
  const notifications = entry.kind === 'bundle' ? entry.notifications : [entry.notification]
  const lead = notifications[0] as InboxNotification
  const isBundle = entry.kind === 'bundle'
  const kind = itemKindLabel(lead.item.kind)
  const appName = notificationAppLabel(lead.app)
  const blocked = state?.status === 'deleted' || state?.status === 'no-access'
  const content = state?.status === 'ready' ? state.content : null
  const framework: InboxFramework = isBundle ? 'timeline' : (content?.framework ?? 'generic')
  const showBare = !isBundle && (content?.framework === 'bare' || (!content && lead.framework === 'bare'))
  const generic = !isBundle && !showBare && (!content || framework === 'generic')
  const decision = !isBundle && !blocked ? decisionArea(lead, answer, onAnswer, now) : null
  const sender = lead.sender.kind === 'none' ? null : lead.sender.name
  const meta = `${notificationTimeLabel(lead.createdAt, now)} · ${appName} · ${NOTIFICATION_BUCKET_LABELS[lead.bucket]}${
    isBundle ? ` · ${notifications.length} updates` : ''
  }${quickAnswerNote(answer)}`

  const said: NotificationDetailMessage[] =
    generic && lead.preview && state?.status !== 'no-access'
      ? [
          {
            who: { name: sender ?? appName, imageUrl: lead.sender.kind === 'none' ? null : lead.sender.imageUrl },
            when: notificationTimeLabel(lead.createdAt, now),
            text: lead.preview,
          },
        ]
      : []
  const thread = content?.blocks.message ?? said
  const canReply = Boolean(onReply) && !blocked && (framework === 'conversation' || said.length > 0)

  return (
    <div className={cn('flex max-w-[640px] flex-col gap-6 px-10 pb-12 pt-8', className)}>
      <div className="flex items-start gap-4">
        <NotificationPicture
          senders={isBundle ? uniqueSenders(notifications) : [lead.sender]}
          app={lead.app}
          size={56}
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <NotificationSentence
            className="text-[19px] leading-[27px]"
            segments={isBundle ? bundleSentence(notifications) : lead.sentence}
          />
          <span className="text-[13px] leading-[18px] text-muted-foreground">{meta}</span>
        </div>
        <NotificationDetailMenu
          openText={blocked ? null : openLabel(lead)}
          unread={unread}
          unfollowText={lead.bucket === 'following' && onUnfollow && !unfollowed ? `Unfollow ${kind}` : null}
          onOpen={() => onOpen?.(lead, 'primary')}
          onToggleRead={onToggleRead}
          onUnfollow={onUnfollow}
        />
      </div>

      {state?.status === 'deleted' ? (
        <StateBanner
          icon={Trash2}
          title={`This ${kind} was deleted.`}
          text={state.note ?? 'There is nothing to open.'}
        />
      ) : state?.status === 'no-access' ? (
        <StateBanner
          icon={Lock}
          title={`You no longer have access to this ${kind}.`}
          text={`What was written is hidden. Ask ${sender ?? 'an admin'} to add you back.`}
        />
      ) : state?.status === 'unavailable' ? (
        <StateBanner
          icon={WifiOff}
          title={`Couldn’t load details from ${appName}.`}
          text={`You can still open it in ${appName}.`}
          action={onRetry ? { label: 'Retry', onClick: onRetry } : undefined}
        />
      ) : null}

      {decision}

      {showBare ? (
        <span className="text-sm text-muted-foreground">That’s everything for this notification.</span>
      ) : isBundle ? (
        <TimelineBlock notifications={notifications} now={now} />
      ) : (
        <DetailBlocks
          blocks={content?.blocks ?? {}}
          thread={thread}
          reply={
            canReply ? (
              <ReplyBox
                key={lead.id}
                notification={lead}
                appName={appName}
                placeholder={`Reply to ${thread.at(-1)?.who.name ?? sender ?? 'them'}…`}
                onReply={onReply as NonNullable<typeof onReply>}
              />
            ) : null
          }
        />
      )}

      {generic && !blocked ? (
        <ItemCard notification={lead} onOpen={onOpen} />
      ) : !showBare && !blocked ? (
        <OpenButtons notification={lead} outline={Boolean(decision) && lead.actions.length > 0} onOpen={onOpen} />
      ) : null}

      {lead.bucket === 'following' ? (
        unfollowed ? (
          <div
            role="status"
            className="flex items-center gap-3 rounded-[10px] bg-muted px-4 py-3 text-sm text-foreground/80">
            <span className="flex-1">
              You unfollowed <strong className="text-foreground">{lead.item.label}</strong>. You won’t get updates on
              it.
            </span>
            {onFollowAgain ? (
              <button
                type="button"
                onClick={onFollowAgain}
                className="h-8 shrink-0 rounded-lg px-3 text-sm font-semibold text-link hover:text-link-hover">
                Undo
              </button>
            ) : null}
          </div>
        ) : onUnfollow ? (
          <div className="flex items-center gap-3 border-t border-border pt-4 text-sm text-muted-foreground">
            <span className="flex-1">You get this because you follow this {kind}.</span>
            <button
              type="button"
              onClick={onUnfollow}
              className={cn(BUTTON, OUTLINE, 'h-[34px] rounded-lg px-3.5 text-sm')}>
              Unfollow {kind}
            </button>
          </div>
        ) : null
      ) : null}
    </div>
  )
}

function decisionArea(
  notification: InboxNotification,
  answer: NotificationQuickAnswer | undefined,
  onAnswer: ((action: NotificationQuickAction) => void) | undefined,
  now: Date,
): React.ReactNode {
  const resolved = resolvedLine(notification, now)
  if (resolved) {
    return <OutcomeBox text={resolved} />
  }
  if (answer?.failed) {
    return (
      <div role="alert" className="rounded-[10px] bg-destructive/10 px-4 py-3 text-[15px] text-destructive">
        {answer.text}
      </div>
    )
  }
  if (answer && answer.phase !== 'done') {
    return <OutcomeBox text={answer.text} fading={answer.phase === 'fading'} />
  }
  if (answer || notification.actions.length === 0 || !onAnswer) {
    return null
  }
  return (
    <div className="flex gap-3">
      {notification.actions.map((action) => (
        <button
          key={action}
          type="button"
          onClick={() => onAnswer(action)}
          className={cn(
            BUTTON,
            'h-11 w-[180px] rounded-[10px] text-[15px]',
            action === 'approve'
              ? 'bg-primary text-primary-foreground hover:bg-primary/90'
              : 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
          )}>
          {NOTIFICATION_ACTION_LABELS[action].label}
        </button>
      ))}
    </div>
  )
}

function OutcomeBox({ text, fading = false }: { text: string; fading?: boolean }) {
  return (
    <div
      role="status"
      className={cn(
        'flex items-center gap-2.5 rounded-[10px] bg-success px-4 py-3 text-[15px] text-success-foreground transition-opacity duration-500',
        fading && 'opacity-0',
      )}>
      <Check className="h-[18px] w-[18px] shrink-0" strokeWidth={2.4} />
      {text}
    </div>
  )
}

function StateBanner({
  icon: Icon,
  title,
  text,
  action,
}: {
  icon: React.ComponentType<{ className?: string }>
  title: string
  text: string
  action?: { label: string; onClick: () => void }
}) {
  return (
    <div role="status" className="flex items-start gap-3 rounded-xl border border-border bg-muted px-4 py-3.5">
      <Icon className="mt-px h-5 w-5 shrink-0 text-muted-foreground" />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-[15px] font-semibold leading-[22px] text-foreground">{title}</span>
        <span className="text-sm text-muted-foreground">{text}</span>
      </span>
      {action ? (
        <button type="button" onClick={action.onClick} className={cn(BUTTON, OUTLINE, 'h-8 rounded-lg px-3 text-sm')}>
          {action.label}
        </button>
      ) : null}
    </div>
  )
}

/** The blocks an app may send, always in this order. */
function DetailBlocks({
  blocks,
  thread,
  reply,
}: {
  blocks: NotificationDetailBlocks
  thread: NotificationDetailMessage[]
  reply: React.ReactNode
}) {
  return (
    <>
      {blocks.change ? (
        <div className="flex items-center gap-2.5 rounded-[10px] bg-muted px-4 py-3 text-sm">
          <span className="min-w-20 text-muted-foreground">{blocks.change.label}</span>
          <span className="text-foreground/70">{blocks.change.from}</span>
          <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" aria-label="to" />
          <span className="font-semibold text-foreground">{blocks.change.to}</span>
        </div>
      ) : null}
      {thread.length > 0 || reply ? <MessageThread messages={thread} footer={reply} /> : null}
      {blocks.text ? (
        <p className="m-0 whitespace-pre-line text-[15px] leading-6 text-foreground/80">{blocks.text}</p>
      ) : null}
      {blocks.checklist ? (
        <div className="flex flex-col gap-2.5">
          <span className="text-[13px] font-semibold text-muted-foreground">{blocks.checklist.title}</span>
          {blocks.checklist.rows.map(([label, done], index) => (
            <span
              key={index}
              className={cn('flex items-center gap-2.5 text-[15px]', done ? 'text-foreground/80' : 'text-foreground')}>
              {done ? (
                <Check className="h-4 w-4 shrink-0 text-success-foreground" strokeWidth={2.4} aria-label="Done" />
              ) : (
                <Circle className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={2.4} aria-label="Not done" />
              )}
              {label}
            </span>
          ))}
        </div>
      ) : null}
      {blocks.list ? (
        <div className="flex flex-col gap-2">
          <span className="text-[13px] font-semibold text-muted-foreground">{blocks.list.title}</span>
          <div className="overflow-hidden rounded-xl border border-border">
            {blocks.list.rows.map(([label, sub], index) => (
              <div
                key={index}
                className="flex items-center gap-3 border-t border-border px-3.5 py-2.5 first:border-t-0">
                <span className={cn('min-w-0 flex-1 text-sm', sub ? 'text-foreground' : 'text-muted-foreground')}>
                  {label}
                </span>
                <span className="shrink-0 text-[13px] text-muted-foreground">{sub}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
      {blocks.errors?.length ? (
        <div className="flex flex-col rounded-[10px] border border-border bg-muted/60 px-4 py-3.5 font-mono text-[13px] leading-[21px] text-destructive">
          {blocks.errors.map((line, index) => (
            <span key={index} className="break-words">
              {line}
            </span>
          ))}
        </div>
      ) : null}
      {blocks.compare ? (
        <div className="overflow-hidden rounded-xl border border-border text-sm">
          <div className="grid grid-cols-2 bg-muted px-3.5 py-2.5 text-xs font-semibold text-muted-foreground">
            <span>{blocks.compare.left}</span>
            <span>{blocks.compare.right}</span>
          </div>
          {blocks.compare.rows.map(([left, right], index) => (
            <div key={index} className="grid grid-cols-2 border-t border-border px-3.5 py-2">
              <span className="text-muted-foreground">{left}</span>
              <span className="text-foreground">{right}</span>
            </div>
          ))}
        </div>
      ) : null}
      {blocks.facts?.length ? (
        <div className="grid grid-cols-[150px_minmax(0,1fr)] gap-x-4 gap-y-3 border-t border-border pt-5 text-[15px] leading-[22px]">
          {blocks.facts.map(([label, value], index) => (
            <React.Fragment key={index}>
              <span className="text-muted-foreground">{label}</span>
              <span className="break-words text-foreground">{value}</span>
            </React.Fragment>
          ))}
        </div>
      ) : null}
    </>
  )
}

function MessageThread({ messages, footer }: { messages: NotificationDetailMessage[]; footer?: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-[18px] rounded-xl border border-border bg-card p-5">
      {messages.map((message, index) => (
        <div key={index} className="flex items-start gap-3">
          <NotificationPicture
            senders={[
              {
                kind: message.who.kind ?? 'person',
                id: message.who.name,
                name: message.who.name,
                imageUrl: message.who.imageUrl,
              },
            ]}
            size={32}
          />
          <span className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="text-sm">
              <span className="font-semibold text-foreground">{message.who.name}</span>
              <span className="ml-2 text-muted-foreground">{message.when}</span>
            </span>
            <span
              className={cn(
                'whitespace-pre-line break-words text-[15px] leading-[23px]',
                index === messages.length - 1 ? 'text-foreground' : 'text-foreground/65',
              )}>
              {message.text}
            </span>
          </span>
        </div>
      ))}
      {footer}
    </div>
  )
}

function ReplyBox({
  notification,
  appName,
  placeholder,
  onReply,
}: {
  notification: InboxNotification
  appName: string
  placeholder: string
  onReply: (notification: InboxNotification, text: string) => Promise<void>
}) {
  const [draft, setDraft] = React.useState('')
  const [sent, setSent] = React.useState<string[]>([])
  const [error, setError] = React.useState<string | null>(null)
  const [sending, setSending] = React.useState(false)

  const send = async () => {
    const text = draft.trim()
    if (!text) {
      setError('Write a reply first.')
      return
    }
    setSending(true)
    try {
      await onReply(notification, text)
      setSent((current) => [...current, text])
      setDraft('')
      setError(null)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Couldn’t send the reply.')
    } finally {
      setSending(false)
    }
  }

  return (
    <>
      {sent.map((text, index) => (
        <div key={index} className="flex items-start gap-3">
          <NotificationPicture senders={[{ kind: 'person', id: 'you', name: 'You' }]} size={32} />
          <span className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="text-sm">
              <span className="font-semibold text-foreground">You</span>
              <span className="ml-2 text-muted-foreground">Just now</span>
            </span>
            <span className="whitespace-pre-line break-words text-[15px] leading-[23px] text-foreground">{text}</span>
          </span>
        </div>
      ))}
      {sent.length > 0 ? <span className="pl-11 text-xs text-muted-foreground">Sent in {appName}</span> : null}
      <div className="flex flex-col gap-1.5 border-t border-border pt-4">
        <div className="flex items-center gap-2.5">
          <input
            value={draft}
            aria-label="Reply"
            placeholder={placeholder}
            onChange={(event) => {
              setDraft(event.target.value)
              setError(null)
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                void send()
              }
            }}
            className="h-10 min-w-0 flex-1 rounded-[10px] border border-input bg-background px-3 text-sm text-foreground outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring"
          />
          <button
            type="button"
            disabled={sending}
            onClick={() => void send()}
            className={cn(
              BUTTON,
              'h-10 rounded-[10px] bg-primary px-4 text-sm text-primary-foreground hover:bg-primary/90',
            )}>
            Send
          </button>
        </div>
        {error ? (
          <span role="alert" className="text-[13px] text-destructive">
            {error}
          </span>
        ) : null}
      </div>
    </>
  )
}

function TimelineBlock({ notifications, now }: { notifications: InboxNotification[]; now: Date }) {
  return (
    <div className="flex flex-col rounded-xl border border-border bg-card">
      {notifications.map((notification) => (
        <div
          key={notification.id}
          className="flex items-center gap-3 border-t border-border px-4 py-3 first:border-t-0">
          <NotificationPicture senders={[notification.sender]} size={32} />
          <span className="min-w-0 flex-1 text-[15px] leading-[22px] text-foreground/80">
            {notification.sender.kind === 'none' ? null : (
              <span className="mr-1.5 font-semibold text-foreground">{notification.sender.name}</span>
            )}
            {bundleLineText(notification)}
          </span>
          <span className="shrink-0 text-[13px] text-muted-foreground">
            {notificationTimeLabel(notification.createdAt, now)}
          </span>
        </div>
      ))}
    </div>
  )
}

function OpenButtons({
  notification,
  outline,
  onOpen,
}: {
  notification: InboxNotification
  outline: boolean
  onOpen?: (notification: InboxNotification, target: 'primary' | 'secondary') => void
}) {
  const secondary = secondaryOpenLabel(notification.item)
  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        onClick={() => onOpen?.(notification, 'primary')}
        className={cn(BUTTON, 'h-[42px] rounded-[10px] px-[18px] text-[15px]', outline ? OUTLINE : PRIMARY_OPEN)}>
        {openLabel(notification)}
        <ArrowRight className="h-4 w-4" />
      </button>
      {secondary ? (
        <button
          type="button"
          onClick={() => onOpen?.(notification, 'secondary')}
          className="h-[38px] px-1.5 text-sm font-semibold text-link hover:text-link-hover">
          {secondary}
        </button>
      ) : null}
    </div>
  )
}

/** Generic framework: the app tile, "App · Kind", the item, and Open in {App}. */
function ItemCard({
  notification,
  onOpen,
}: {
  notification: InboxNotification
  onOpen?: (notification: InboxNotification, target: 'primary' | 'secondary') => void
}) {
  const kind = itemKindLabel(notification.item.kind)
  const secondary = secondaryOpenLabel(notification.item)
  return (
    <div className="flex flex-wrap items-center gap-3.5 rounded-xl border border-border bg-card px-4 py-3.5">
      <NotificationAppTile app={notification.app} />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="text-[13px] text-muted-foreground">
          {notificationAppLabel(notification.app)} · {kind.charAt(0).toUpperCase() + kind.slice(1)}
        </span>
        <span className="truncate text-[15px] font-semibold text-foreground">{notification.item.label}</span>
      </span>
      <button
        type="button"
        onClick={() => onOpen?.(notification, 'primary')}
        className={cn(BUTTON, PRIMARY_OPEN, 'h-[38px] rounded-[10px] px-3.5 text-sm')}>
        {openLabel(notification)}
        <ArrowRight className="h-[15px] w-[15px]" />
      </button>
      {secondary ? (
        <button
          type="button"
          onClick={() => onOpen?.(notification, 'secondary')}
          className="h-[38px] px-1.5 text-sm font-semibold text-link hover:text-link-hover">
          {secondary}
        </button>
      ) : null}
    </div>
  )
}

function NotificationDetailMenu({
  openText,
  unread,
  unfollowText,
  onOpen,
  onToggleRead,
  onUnfollow,
}: {
  openText: string | null
  unread: boolean
  unfollowText: string | null
  onOpen: () => void
  onToggleRead?: () => void
  onUnfollow?: () => void
}) {
  if (!openText && !onToggleRead && !unfollowText) {
    return null
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="More actions"
          className="h-[34px] w-[38px] shrink-0 rounded-lg text-[17px] leading-none text-muted-foreground transition-colors hover:bg-muted data-[state=open]:bg-muted">
          ···
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[190px]">
        {openText ? <DropdownMenuItem onClick={onOpen}>{openText}</DropdownMenuItem> : null}
        {onToggleRead ? (
          <DropdownMenuItem onClick={onToggleRead}>{unread ? 'Mark as read' : 'Mark as unread'}</DropdownMenuItem>
        ) : null}
        {unfollowText ? <DropdownMenuItem onClick={onUnfollow}>{unfollowText}</DropdownMenuItem> : null}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/** Grey blocks in the shape of what is coming: header, buttons, facts, Open. */
export function NotificationDetailSkeleton({ className }: { className?: string }) {
  const bar = 'block animate-pulse rounded-md bg-muted'
  return (
    <div aria-busy="true" className={cn('flex max-w-[640px] flex-col gap-6 px-10 pb-12 pt-8', className)}>
      <div className="flex items-center gap-4">
        <span className={cn(bar, 'h-14 w-14 shrink-0 rounded-full')} />
        <span className="flex flex-1 flex-col gap-2">
          <span className={cn(bar, 'h-[18px] w-[85%]')} />
          <span className={cn(bar, 'h-[18px] w-[55%]')} />
          <span className={cn(bar, 'h-3 w-[30%]')} />
        </span>
      </div>
      <div className="flex flex-col gap-3 rounded-xl border border-border p-5">
        <span className={cn(bar, 'h-3 w-[40%]')} />
        <span className={cn(bar, 'h-3.5 w-[95%]')} />
        <span className={cn(bar, 'h-3.5 w-[70%]')} />
      </div>
      <div className="flex flex-col gap-3">
        {[0, 1, 2].map((row) => (
          <span key={row} className="flex gap-4">
            <span className={cn(bar, 'h-3.5 w-[130px]')} />
            <span className={cn(bar, 'h-3.5 w-[45%]')} />
          </span>
        ))}
      </div>
      <span className={cn(bar, 'h-[42px] w-[170px] rounded-[10px]')} />
    </div>
  )
}

/** The right side when the Following group line is selected: open the tab, or mark the lot read. */
export function NotificationGroupDetail({
  group,
  isUnread,
  now = new Date(),
  className,
  onOpenFollowing,
  onToggleRead,
}: {
  group: InboxGroupEntry
  isUnread: (entry: InboxItemEntry) => boolean
  now?: Date
  className?: string
  onOpenFollowing: (entry?: InboxItemEntry) => void
  onToggleRead?: (read: boolean) => void
}) {
  const unread = group.entries.filter(isUnread).length
  const meta = [
    group.dayLabel,
    `${group.entries.length} things`,
    unread ? `${unread} unread` : null,
    ...groupAppLabels(group),
  ]
    .filter(Boolean)
    .join(' · ')
  return (
    <div className={cn('flex max-w-[640px] flex-col gap-6 px-10 pb-12 pt-8', className)}>
      <div className="flex items-start gap-4">
        <NotificationGenericCircle size={56} />
        <span className="flex min-w-0 flex-1 flex-col gap-1.5">
          <span className="text-[19px] leading-[27px] text-foreground/80">
            <span className="font-semibold text-foreground">Updates</span> on things you follow
          </span>
          <span className="text-[13px] text-muted-foreground">{meta}</span>
        </span>
      </div>
      <div className="flex gap-3">
        <button
          type="button"
          onClick={() => onOpenFollowing()}
          className={cn(BUTTON, PRIMARY_OPEN, 'h-[42px] rounded-[10px] px-[18px] text-[15px]')}>
          Open Following
          <ArrowRight className="h-4 w-4" />
        </button>
        {onToggleRead ? (
          <button
            type="button"
            onClick={() => onToggleRead(unread > 0)}
            className={cn(BUTTON, OUTLINE, 'h-[42px] rounded-[10px] px-[18px] text-[15px]')}>
            {unread ? 'Mark all read' : 'Mark all unread'}
          </button>
        ) : null}
      </div>
      <div className="flex flex-col gap-1">
        {group.entries.map((entry) => {
          const notifications = entry.kind === 'bundle' ? entry.notifications : [entry.notification]
          const lead = notifications[0] as InboxNotification
          const rowUnread = isUnread(entry)
          return (
            <button
              key={entry.id}
              type="button"
              onClick={() => onOpenFollowing(entry)}
              className={cn(
                'flex w-full items-start gap-3 rounded-[10px] p-3 text-left transition-colors hover:bg-muted',
                rowUnread && 'bg-primary/10',
              )}>
              <NotificationPicture
                senders={entry.kind === 'bundle' ? uniqueSenders(notifications) : [lead.sender]}
                app={lead.app}
                size={36}
              />
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <NotificationSentence
                  className="text-[15px] leading-[22px]"
                  segments={entry.kind === 'bundle' ? bundleSentence(notifications) : lead.sentence}
                />
                {entry.kind === 'row' && lead.preview ? (
                  <span className="truncate text-sm text-muted-foreground">“{lead.preview}”</span>
                ) : null}
                <span className="text-xs text-muted-foreground">
                  {notificationTimeLabel(lead.createdAt, now)}
                  {entry.kind === 'bundle' ? ` · ${notifications.length} updates` : ''}
                </span>
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

/** Shown in the right pane when nothing is picked, or there is nothing at all. */
export function NotificationDetailEmpty({ caughtUp = false }: { caughtUp?: boolean }) {
  if (!caughtUp) {
    return (
      <div className="grid h-full min-h-[300px] place-items-center text-sm text-muted-foreground">
        Select a notification to see it here.
      </div>
    )
  }
  return (
    <div className="grid h-full min-h-[300px] place-items-center">
      <span className="flex flex-col items-center gap-2.5 text-muted-foreground">
        <CircleCheck className="h-10 w-10 text-success-foreground" strokeWidth={1.8} />
        <span className="text-lg font-semibold text-foreground">You’re all caught up</span>
        <span className="text-sm">New notifications from every app show up here.</span>
      </span>
    </div>
  )
}
